// src/pages/CommunityPage.js

import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ThemeContext } from '../context/ThemeContext';
import { communityActivityService, membershipApplicationService } from '../api';
import useApi from '../hooks/useApi';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import { toast } from 'react-toastify';

import communityHeroPath from '../assets/images/com.jpeg';
import collabImagePath from '../assets/images/pc.png'; // À remplacer par votre image
import learningImagePath from '../assets/images/ap.png'; // À remplacer par votre image
import earningImagePath from '../assets/images/op.png'; // À remplacer par votre image
import hackathonImagePath from '../assets/images/rr.png'; // À remplacer par votre image
import Apropos from '../assets/images/app.jpeg';
import FeaturedEvent from './FeaturedEvent';


// Styled components
const CommunitySection = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroContainer = styled.div`
  position: relative;
  height: 80vh;
  min-height: 600px;
  border-radius: 0;
  overflow: hidden;
  margin-bottom: 0;
  
  @media (max-width: 768px) {
    height: 60vh;
    min-height: 500px;
  }
`;

const HeroImage = styled.div`
  width: 100%;
  height: 100vh;
  background-image: linear-gradient(
    rgba(0, 0, 0, 0.7),
    rgba(0, 0, 0, 0.7)
  ), url(${props => props.src || '/community-hero.jpg'});
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem;

  @media (max-width: 768px) {
    background-position: center top;
    background-size: cover;
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    background-position: center top;
    background-size: contain;
    height: auto;
    padding: 1rem;
  }
`;


const HeroTitle = styled.h1`
  font-size: 4rem;
  color: white;
  margin-bottom: 1.5rem;
  font-weight: 800;
  
  span {
    color: #56C8FF;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const HeroText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.4rem;
  max-width: 700px;
  margin-bottom: 2.5rem;
  line-height: 1.7;
`;

const GradientButton = styled(Button)`
  background: linear-gradient(90deg, #56C8FF 0%, #FF7A00 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(90deg, #FF7A00 0%, #56C8FF 100%);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const AboutSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.backgroundSecondary};
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutContent = styled.div`
  h2 {
    font-size: 2.5rem;
    color: ${props => props.theme.primary};
    margin-bottom: 1.5rem;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    color: ${props => props.theme.text};
    font-size: 1.1rem;
    line-height: 1.7;
    margin-bottom: 1.5rem;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 2rem 0;
    
    li {
      position: relative;
      padding-left: 2rem;
      margin-bottom: 1rem;
      color: ${props => props.theme.text};
      font-size: 1.1rem;
      line-height: 1.5;
      
      &:before {
        content: '✓';
        position: absolute;
        left: 0;
        top: 0;
        color: ${props => props.theme.primary};
        font-weight: bold;
      }
    }
  }
`;

const AboutImage = styled.div`
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  height: 500px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center; /* bien centrer les visages */
  }

  @media (max-width: 992px) {
    height: 350px;
    img {
      object-position: center top;
    }
  }

  @media (max-width: 768px) {
    height: auto; /* s'adapte à l'image */
    img {
      height: auto;
      object-fit: contain; /* ou 'cover' si tu veux qu'elle remplisse quand même */
      object-position: center top;
    }
  }
`;


const BenefitsSection = styled.section`
  padding: 6rem 2rem;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 10px;
  overflow: hidden;
  box-shadow: ${props => props.theme.card.shadow};
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const BenefitImage = styled.div`
  height: 250px;
  background-image: url(${props => props.src || '/benefit-default.jpg'});
  background-size: cover;
  background-position: center;
`;

const BenefitContent = styled.div`
  padding: 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.primary};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${props => props.theme.text};
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    flex: 1;
  }
`;

const ActivitySection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.backgroundSecondary};
`;

const ActivityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ActivityCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 10px;
  overflow: hidden;
  box-shadow: ${props => props.theme.card.shadow};
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ActivityImage = styled.div`
  height: 200px;
  background-image: url(${props => props.src || '/activity-default.jpg'});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const ActivityType = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.3rem 0.8rem;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ActivityContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  
  h3 {
    font-size: 1.3rem;
    color: ${props => props.theme.text};
    margin-bottom: 0.5rem;
  }
  
  .date-location {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
    font-size: 0.9rem;
    color: ${props => props.theme.textSecondary};
    
    div {
      display: flex;
      align-items: center;
      
      i {
        margin-right: 0.5rem;
        color: ${props => props.theme.primary};
      }
    }
  }
  
  p {
    color: ${props => props.theme.text};
    opacity: 0.8;
    margin-bottom: 1.5rem;
    flex: 1;
  }
`;

const TestimonialsSection = styled.section`
  padding: 6rem 2rem;
`;

const TestimonialsSlider = styled.div`
  max-width: 900px;
  margin: 3rem auto;
`;

const TestimonialCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 10px;
  padding: 3rem;
  text-align: center;
  box-shadow: ${props => props.theme.card.shadow};
  margin: 0 1rem;
  
  .quote {
    font-size: 1.3rem;
    line-height: 1.7;
    font-style: italic;
    color: ${props => props.theme.text};
    margin-bottom: 2rem;
    position: relative;
    
    &:before, &:after {
      content: '"';
      font-size: 4rem;
      position: absolute;
      opacity: 0.1;
      font-family: serif;
    }
    
    &:before {
      top: -2rem;
      left: -1rem;
    }
    
    &:after {
      bottom: -3rem;
      right: -1rem;
    }
  }
  
  .author {
    margin-top: 1.5rem;
    
    .name {
      font-size: 1.2rem;
      font-weight: bold;
      color: ${props => props.theme.primary};
    }
    
    .role {
      font-size: 0.9rem;
      color: ${props => props.theme.textSecondary};
    }
  }
`;

const StatsSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #0b1941 0%, #000c24 100%);
  color: white;
  text-align: center;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const StatItem = styled.div`
  .number {
    font-size: 3.5rem;
    font-weight: 800;
    color: #56C8FF;
    margin-bottom: 0.5rem;
  }
  
  .label {
    font-size: 1.2rem;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
  }
`;

const JoinSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.background};
`;

const JoinFormContainer = styled.div`
  max-width: 700px;
  margin: 3rem auto;
  background: ${props => props.theme.card.background};
  border-radius: 10px;
  padding: 3rem;
  box-shadow: ${props => props.theme.card.shadow};
  
  h3 {
    font-size: 1.8rem;
    color: ${props => props.theme.primary};
    margin-bottom: 1.5rem;
    text-align: center;
  }
  
  p {
    color: ${props => props.theme.text};
    margin-bottom: 2rem;
    text-align: center;
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    color: ${props => props.theme.text};
    font-weight: 500;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid ${props => props.theme.text}30;
    border-radius: 4px;
    background: ${props => props.theme.background};
    color: ${props => props.theme.text};
    font-family: inherit;
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.primary};
    }
  }
  
  textarea {
    min-height: 120px;
    resize: vertical;
  }
  
  .error-message {
    color: ${props => props.theme.error};
    font-size: 0.9rem;
    margin-top: 0.5rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  padding: 1rem;
  margin-top: 1rem;
  font-weight: 600;
  font-size: 1.1rem;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
  font-size: 1.2rem;
`;

const FaqSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.backgroundSecondary};
`;

const FaqContainer = styled.div`
  max-width: 800px;
  margin: 3rem auto;
`;

const FaqItem = styled.div`
  margin-bottom: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.text}20;
  padding-bottom: 1.5rem;
  
  &:last-child {
    border-bottom: none;
  }
  
  .question {
    font-size: 1.3rem;
    font-weight: 600;
    color: ${props => props.theme.text};
    margin-bottom: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    
    &:hover {
      color: ${props => props.theme.primary};
    }
    
    i {
      transition: transform 0.3s ease;
      transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
    }
  }
  
  .answer {
    color: ${props => props.theme.textSecondary};
    font-size: 1.1rem;
    line-height: 1.7;
    height: ${props => props.isOpen ? 'auto' : '0'};
    overflow: hidden;
    opacity: ${props => props.isOpen ? '1' : '0'};
    transition: opacity 0.3s ease;
    padding-left: 1rem;
    border-left: 2px solid ${props => props.theme.primary}50;
  }
`;

const CommunityPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Références pour l'animation
  const [benefitsRef, benefitsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const [activitiesRef, activitiesInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const [testimonialRef, testimonialInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  // Charger les activités communautaires
  const { 
    data: activitiesData, 
    loading: activitiesLoading, 
    error: activitiesError 
  } = useApi(() => communityActivityService.getAll({ limit: 3 }), []);
  
  // État du formulaire d'inscription
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    skills: '',
    experience: '',
    motivation: '',
    github: '',
    linkedin: '',
    portfolio: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Gestion des FAQs
  const [faqState, setFaqState] = useState({
    faq1: false,
    faq2: false,
    faq3: false,
    faq4: false,
    faq5: false
  });
  
  const toggleFaq = (faqId) => {
    setFaqState(prev => ({
      ...prev,
      [faqId]: !prev[faqId]
    }));
  };
  
  // Fonction de validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formState.firstName) errors.firstName = t('firstNameRequired');
    if (!formState.lastName) errors.lastName = t('lastNameRequired');
    if (!formState.email) errors.email = t('emailRequired');
    else if (!/\S+@\S+\.\S+/.test(formState.email)) errors.email = t('invalidEmail');
    if (!formState.skills) errors.skills = t('skillsRequired');
    if (!formState.motivation) errors.motivation = t('motivationRequired');
    
    return errors;
  };
  
  // Traduction du type d'activité
  const getActivityTypeLabel = (type) => {
    switch (type) {
      case 'hackathon':
        return t('hackathon');
      case 'workshop':
        return t('workshop');
      case 'meetup':
        return t('meetup');
      case 'webinar':
        return t('webinar');
      case 'conference':
        return t('conference');
      case 'competition':
        return t('competition');
      default:
        return type;
    }
  };
  
  // Formatage de la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(navigator.language, options);
  };
  
  // Gestion des changements dans le formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Version finale du handleSubmit pour CommunityPage.js
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation du formulaire
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Scroll jusqu'à la première erreur
      const firstErrorField = Object.keys(errors)[0];
      document.getElementById(firstErrorField)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Préparation des données de demande d'adhésion
      // Utiliser FormData pour être compatible avec multipart/form-data
      const formData = new FormData();
      
      // Ajouter tous les champs du formulaire
      formData.append('first_name', formState.firstName);
      formData.append('last_name', formState.lastName);
      formData.append('email', formState.email);
      if (formState.phone) formData.append('phone', formState.phone);
      formData.append('motivation', formState.motivation);
      formData.append('skills', formState.skills);
      if (formState.experience) formData.append('experiences', formState.experience);
      if (formState.github) formData.append('github_profile', formState.github);
      if (formState.linkedin) formData.append('linkedin_profile', formState.linkedin);
      if (formState.portfolio) formData.append('portfolio', formState.portfolio);
      
      console.log("Envoi du formulaire...");
      
      // Envoi de la demande d'adhésion
      const response = await membershipApplicationService.create(formData);
      
      console.log('Réponse reçue:', response);
      
      // Notification de succès
      toast.success(t('applicationSubmittedSuccess') || "Votre candidature a été soumise avec succès!");
      
      // Réinitialiser le formulaire
      setFormState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        skills: '',
        experience: '',
        motivation: '',
        github: '',
        linkedin: '',
        portfolio: ''
      });
      
      // Redirection ou affichage d'un message de confirmation
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting application:', error);
      
      // Afficher les détails complets de l'erreur
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);
        console.error('Error data:', error.response.data);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      
      // Notification d'erreur
      let errorMessage = t('applicationSubmittedError') || "Une erreur s'est produite lors de la soumission de votre candidature.";
      
      if (error.response?.data) {
        const data = error.response.data;
        if (data.error) errorMessage = data.error;
        else if (data.message) errorMessage = data.message;
        else if (data.detail) errorMessage = data.detail;
        else if (typeof data === 'string') errorMessage = data;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };
  
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6 } }
  };
  
  // Traitement des activités pour affichage
  const getActivitiesToDisplay = () => {
    if (!activitiesData?.results) return [];
    
    return activitiesData.results
      .filter(activity => new Date(activity.date) >= new Date())
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 3);
  };
  
  const upcomingActivities = getActivitiesToDisplay();
  
  return (
    <>
      {/* Hero Section */}
      <HeroContainer>
        <HeroImage src={communityHeroPath}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <HeroTitle>La <span>Communauté</span> Propenta</HeroTitle>
            <HeroText>
              Rejoignez un écosystème innovant de développeurs, designers et passionnés de technologie pour apprendre, collaborer et grandir ensemble.
            </HeroText>
            <GradientButton
              onClick={() => {
                document.getElementById('join-form').scrollIntoView({ 
                  behavior: 'smooth' 
                });
              }}
            >
              Rejoindre la communauté
            </GradientButton>
          </motion.div>
        </HeroImage>
      </HeroContainer>
      
      {/* About Community Section */}
      <AboutSection theme={theme}>
        <Container>
          <AboutGrid>
            <AboutContent theme={theme}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h2>À propos de notre communauté</h2>
                <p>
                  La communauté Propenta est un espace dynamique où les passionnés de technologie se réunissent pour partager leurs connaissances, collaborer sur des projets et développer de nouvelles compétences.
                </p>
                <p>
                  Notre communauté est inclusive et accueille des membres de tous niveaux, des débutants aux experts.
                </p>
                <ul>
                  <li>Accès à des formations de qualité et à des ressources exclusives</li>
                  <li>Opportunités de participer à des projets concrets avec des clients réels</li>
                  <li>Possibilité de rejoindre l'équipe technique de Propenta après validation</li>
                  <li>Programmes de récompenses basés sur les contributions</li>
                  <li>Apprentissage des méthodologies de travail modernes (Scrum, Agile)</li>
                </ul>
              </motion.div>
            </AboutContent>
            <AboutImage>
              <motion.img 
                src={Apropos} 
                alt="Communauté Propenta" 
                initial={{ scale: 1.1, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8 }}
              />
            </AboutImage>
          </AboutGrid>
        </Container>
      </AboutSection>
      
      {/* Benefits Section */}
      <BenefitsSection>
        <Container>
          <SectionTitle
            title="Avantages de rejoindre notre communauté"
            subtitle="Découvrez tout ce que vous pouvez gagner en devenant membre"
          />
          
          <BenefitsGrid ref={benefitsRef}>
            <BenefitCard 
              theme={theme}
              variants={fadeInUp}
              initial="hidden" 
              animate={benefitsInView ? "visible" : "hidden"}
              transition={{ delay: 0.1 }}
            >
              <BenefitImage src={learningImagePath} />
              <BenefitContent theme={theme}>
                <h3>Apprentissage continu</h3>
                <p>
                  Accédez à des formations exclusives, des ateliers pratiques et des ressources pédagogiques pour développer vos compétences techniques. Nos mentors expérimentés vous guideront tout au long de votre parcours d'apprentissage.
                </p>
                <Button variant="outline" size="small"onClick={() => navigate('/blog')}>Voir les Articles</Button>
              </BenefitContent>
            </BenefitCard>
            
            <BenefitCard 
              theme={theme}
              variants={fadeInUp}
              initial="hidden" 
              animate={benefitsInView ? "visible" : "hidden"}
              transition={{ delay: 0.2 }}
            >
              <BenefitImage src={collabImagePath} />
              <BenefitContent theme={theme}>
                <h3>Projets concrets</h3>
                <p>
                  Participez à des projets réels pour des clients et développez votre expérience professionnelle. Ces projets vous permettront d'appliquer vos connaissances et d'enrichir votre portfolio.
                </p>
                <Button variant="outline" size="small" onClick={() => navigate('/projects')}>Voir les projets</Button>
              </BenefitContent>
            </BenefitCard>
            
            <BenefitCard 
              theme={theme}
              variants={fadeInUp}
              initial="hidden" 
              animate={benefitsInView ? "visible" : "hidden"}
              transition={{ delay: 0.3 }}
            >
              <BenefitImage src={earningImagePath} />
              <BenefitContent theme={theme}>
                <h3>Opportunités d'emploi</h3>
                <p>
                  Les membres les plus actifs et talentueux ont la possibilité d'intégrer l'équipe technique de Propenta. Nous recrutons directement parmi notre communauté pour constituer des équipes performantes.
                </p>
                <Button variant="outline" size="small">Voir les postes</Button>
              </BenefitContent>
            </BenefitCard>
            
            <BenefitCard 
              theme={theme}
              variants={fadeInUp}
              initial="hidden" 
              animate={benefitsInView ? "visible" : "hidden"}
              transition={{ delay: 0.4 }}
            >
              <BenefitImage src={hackathonImagePath} />
              <BenefitContent theme={theme}>
                <h3>Récompenses et rémunération</h3>
                <p>
                  Recevez des récompenses et des rémunérations pour vos contributions aux projets clients. Notre programme de récompenses valorise l'implication et la qualité du travail de chaque membre.
                </p>
                <Button variant="outline" size="small">Programme de récompenses</Button>
              </BenefitContent>
            </BenefitCard>
          </BenefitsGrid>
        </Container>
      </BenefitsSection>
      
      {/* Activities Section */}
      <ActivitySection theme={theme}>
        <Container>
          <SectionTitle
            title="Activités à venir"
            subtitle="Participez à nos prochains événements et ateliers"
          />
          {/* <FeaturedEvent theme={theme} /> */}
          <div ref={activitiesRef}>
            {activitiesLoading ? (
              <LoadingIndicator>
                <svg width="50" height="50" viewBox="0 0 50 50">
                  <circle 
                    cx="25" 
                    cy="25" 
                    r="20" 
                    fill="none" 
                    stroke={theme.primary} 
                    strokeWidth="3" 
                    strokeLinecap="round"
                  >
                    <animateTransform 
                      attributeName="transform" 
                      type="rotate" 
                      from="0 25 25" 
                      to="360 25 25" 
                      dur="1.5s" 
                      repeatCount="indefinite"
                    />
                    <animate 
                      attributeName="stroke-dasharray" 
                      from="1, 150" 
                      to="90, 150" 
                      dur="1.5s" 
                      repeatCount="indefinite"
                    />
                    <animate 
                      attributeName="stroke-dashoffset" 
                      from="0" 
                      to="-35" 
                      dur="1.5s" 
                      repeatCount="indefinite"
                    />
                  </circle>
                </svg>
              </LoadingIndicator>
            ) : activitiesError ? (
              <div style={{ padding: '1.5rem 0' }}>
                <p style={{ color: theme.error }}>{activitiesError}</p>
              </div>
            ) : !upcomingActivities || upcomingActivities.length === 0 ? (
              <NoResults theme={theme}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <h3>{t('noActivitiesFound')}</h3>
                  <p>{t('checkBackLater')}</p>
                </motion.div>
              </NoResults>
            ) : (
              <ActivityGrid>
                {upcomingActivities.map((activity, index) => (
                  <ActivityCard 
                    key={activity.id}
                    theme={theme}
                    variants={fadeInUp}
                    initial="hidden"
                    animate={activitiesInView ? "visible" : "hidden"}
                    transition={{ delay: index * 0.1 }}
                  >
                    <ActivityImage src={activity.image}>
                      <ActivityType>
                        {getActivityTypeLabel(activity.activity_type)}
                      </ActivityType>
                    </ActivityImage>
                    <ActivityContent theme={theme}>
                      <h3>{activity.title}</h3>
                      <div className="date-location">
                        <div>
                          <i className="far fa-calendar-alt"></i>
                          <span>{formatDate(activity.date)}</span>
                        </div>
                        <div>
                          <i className="fas fa-map-marker-alt"></i>
                          <span>
                            {activity.is_online ? t('online') : activity.location}
                          </span>
                        </div>
                      </div>
                      <p>
                        {activity.description.length > 150 
                          ? `${activity.description.substring(0, 150)}...` 
                          : activity.description}
                      </p>
                      <Button
                        variant="primary"
                        size="small"
                        as={Link}
                        to={`/events/${activity.id}`}
                      >
                        En savoir plus
                      </Button>
                    </ActivityContent>
                  </ActivityCard>
                ))}
              </ActivityGrid>
            )}
            
            <motion.div
              style={{ textAlign: 'center', marginTop: '3rem' }}
              variants={fadeIn}
              initial="hidden"
              animate={activitiesInView ? "visible" : "hidden"}
            >
              <Button
                variant="outline"
                size="large"
                as={Link}
                to="/events"
              >
                Voir tous les événements
              </Button>
            </motion.div>
          </div>
        </Container>
      </ActivitySection>
      
      {/* Testimonials Section */}
      <TestimonialsSection>
        <Container>
          <SectionTitle
            title="Ce que disent nos membres"
            subtitle="Découvrez les expériences de notre communauté"
          />
          
          <TestimonialsSlider ref={testimonialRef}>
            <TestimonialCard 
              theme={theme}
              variants={fadeIn}
              initial="hidden"
              animate={testimonialInView ? "visible" : "hidden"}
            >
              <div className="quote">
                Rejoindre la communauté Propenta a été une décision qui a changé ma carrière. Les formations, le mentorat et les projets réels m'ont permis de développer mes compétences techniques et professionnelles à un niveau que je n'aurais jamais imaginé. Aujourd'hui, je fais partie de l'équipe technique!
              </div>
              <div className="author">
                <div className="name">Sarah L.</div>
                <div className="role">Développeuse Full Stack</div>
              </div>
            </TestimonialCard>
          </TestimonialsSlider>
        </Container>
      </TestimonialsSection>
      
      {/* Stats Section */}
      <StatsSection>
        <Container>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ fontSize: '2.5rem', marginBottom: '1rem' }}
          >
            Notre communauté en chiffres
          </motion.h2>
          
          <StatsGrid>
            <StatItem>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="number">20+</div>
                <div className="label">Membres</div>
              </motion.div>
            </StatItem>
            
            <StatItem>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="number">10+</div>
                <div className="label">Événements par an</div>
              </motion.div>
            </StatItem>
            
            <StatItem>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="number">5+</div>
                <div className="label">Projets actifs</div>
              </motion.div>
            </StatItem>
            
            <StatItem>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="number">3+</div>
                <div className="label">Années d'existence</div>
              </motion.div>
            </StatItem>
          </StatsGrid>
        </Container>
      </StatsSection>
      
      {/* Join Form Section */}
      <JoinSection theme={theme} id="join-form">
        <Container>
          <SectionTitle
            title="Rejoignez notre communauté"
            subtitle="Complétez le formulaire pour soumettre votre candidature"
          />
          
          <JoinFormContainer theme={theme}>
            <h3>Formulaire d'inscription</h3>
            <p>Tous les champs marqués d'un * sont obligatoires</p>
            
            <form onSubmit={handleSubmit}>
              <FormGrid>
                <FormGroup theme={theme}>
                  <label htmlFor="firstName">Prénom *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formState.firstName}
                    onChange={handleInputChange}
                  />
                  {formErrors.firstName && (
                    <div className="error-message">{formErrors.firstName}</div>
                  )}
                </FormGroup>
                
                <FormGroup theme={theme}>
                  <label htmlFor="lastName">Nom *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formState.lastName}
                    onChange={handleInputChange}
                  />
                  {formErrors.lastName && (
                    <div className="error-message">{formErrors.lastName}</div>
                  )}
                </FormGroup>
              </FormGrid>
              
              <FormGrid>
                <FormGroup theme={theme}>
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formState.email}
                    onChange={handleInputChange}
                  />
                  {formErrors.email && (
                    <div className="error-message">{formErrors.email}</div>
                  )}
                </FormGroup>
                
                <FormGroup theme={theme}>
                  <label htmlFor="phone">Téléphone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formState.phone}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </FormGrid>
              
              <FormGroup theme={theme}>
                <label htmlFor="skills">Compétences technologiques *</label>
                <textarea
                  id="skills"
                  name="skills"
                  value={formState.skills}
                  onChange={handleInputChange}
                  placeholder="Listez vos compétences (ex: JavaScript, React, Python, Django...)"
                />
                {formErrors.skills && (
                  <div className="error-message">{formErrors.skills}</div>
                )}
              </FormGroup>
              
              <FormGroup theme={theme}>
                <label htmlFor="experience">Expérience professionnelle</label>
                <textarea
                  id="experience"
                  name="experience"
                  value={formState.experience}
                  onChange={handleInputChange}
                  placeholder="Décrivez brièvement votre expérience professionnelle"
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <label htmlFor="motivation">Motivation pour rejoindre la communauté *</label>
                <textarea
                  id="motivation"
                  name="motivation"
                  value={formState.motivation}
                  onChange={handleInputChange}
                  placeholder="Expliquez pourquoi vous souhaitez rejoindre notre communauté et ce que vous espérez y apporter"
                />
                {formErrors.motivation && (
                  <div className="error-message">{formErrors.motivation}</div>
                )}
              </FormGroup>
              
              <FormGrid>
                <FormGroup theme={theme}>
                  <label htmlFor="github">Profil GitHub</label>
                  <input
                    type="url"
                    id="github"
                    name="github"
                    value={formState.github}
                    onChange={handleInputChange}
                    placeholder="https://github.com/username"
                  />
                </FormGroup>
                
                <FormGroup theme={theme}>
                  <label htmlFor="linkedin">Profil LinkedIn</label>
                  <input
                    type="url"
                    id="linkedin"
                    name="linkedin"
                    value={formState.linkedin}
                    onChange={handleInputChange}
                    placeholder="https://linkedin.com/in/username"
                  />
                </FormGroup>
              </FormGrid>
              
              <FormGroup theme={theme}>
                <label htmlFor="portfolio">Portfolio ou site personnel</label>
                <input
                  type="url"
                  id="portfolio"
                  name="portfolio"
                  value={formState.portfolio}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                />
              </FormGroup>
              
              <SubmitButton
                variant="primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Envoi en cours...' : 'Soumettre ma candidature'}
              </SubmitButton>
            </form>
          </JoinFormContainer>
        </Container>
      </JoinSection>
      
      {/* FAQ Section */}
      <FaqSection theme={theme}>
        <Container>
          <SectionTitle
            title="Questions fréquentes"
            subtitle="Tout ce que vous devez savoir sur notre communauté"
          />
          
          <FaqContainer>
            <FaqItem theme={theme} isOpen={faqState.faq1}>
              <div className="question" onClick={() => toggleFaq('faq1')}>
                Comment fonctionne le processus de sélection ?
                <i className={`fas fa-chevron-down`}></i>
              </div>
              {faqState.faq1 && (
                <div className="answer">
                  Après avoir soumis votre candidature, notre équipe l'examinera dans un délai de 7 jours. Nous évaluons les compétences techniques, la motivation et le potentiel de contribution à la communauté. Les candidats retenus sont ensuite invités à un entretien virtuel pour discuter plus en détail.
                </div>
              )}
            </FaqItem>
            
            <FaqItem theme={theme} isOpen={faqState.faq2}>
              <div className="question" onClick={() => toggleFaq('faq2')}>
                Quels sont les critères pour intégrer l'équipe technique ?
                <i className={`fas fa-chevron-down`}></i>
              </div>
              {faqState.faq2 && (
                <div className="answer">
                  Les membres qui souhaitent rejoindre l'équipe technique de Propenta doivent démontrer leurs compétences à travers une participation active aux projets de la communauté. Nous évaluons la qualité du code, la capacité à collaborer en équipe, la proactivité et l'adhésion à nos valeurs d'entreprise. Un processus de test technique et des entretiens sont organisés pour les candidats présélectionnés.
                </div>
              )}
            </FaqItem>
            
            <FaqItem theme={theme} isOpen={faqState.faq3}>
              <div className="question" onClick={() => toggleFaq('faq3')}>
                Comment sont rémunérées les contributions aux projets ?
                <i className={`fas fa-chevron-down`}></i>
              </div>
              {faqState.faq3 && (
                <div className="answer">
                  Les contributions aux projets clients sont rémunérées selon plusieurs critères : la complexité du travail, le temps investi, la qualité du résultat et l'impact sur le projet. Notre système de rémunération est transparent et basé sur un barème communiqué à tous les membres. Les paiements sont effectués une fois les livrables validés par le client et notre équipe qualité.
                </div>
              )}
            </FaqItem>
            
            <FaqItem theme={theme} isOpen={faqState.faq4}>
              <div className="question" onClick={() => toggleFaq('faq4')}>
                Quels types de formations sont proposés ?
                <i className={`fas fa-chevron-down`}></i>
              </div>
              {faqState.faq4 && (
                <div className="answer">
                  Nous proposons une variété de formations techniques et professionnelles : développement web (frontend, backend), développement mobile, DevOps, cloud computing, intelligence artificielle, ainsi que des formations sur les méthodologies Agile/Scrum, la gestion de projet et le soft skills. Ces formations sont dispensées par des experts de l'industrie et des membres seniors de notre équipe.
                </div>
              )}
            </FaqItem>
            
            <FaqItem theme={theme} isOpen={faqState.faq5}>
              <div className="question" onClick={() => toggleFaq('faq5')}>
                Quel niveau d'engagement est attendu des membres ?
                <i className={`fas fa-chevron-down`}></i>
              </div>
              {faqState.faq5 && (
                <div className="answer">
                  L'engagement varie selon la disponibilité de chacun. Nous comprenons que nos membres peuvent avoir d'autres activités professionnelles ou académiques. Cependant, pour bénéficier pleinement de la communauté, nous recommandons de participer régulièrement aux événements et de contribuer à au moins un projet tous les trimestres. Les membres souhaitant intégrer l'équipe technique devront naturellement démontrer un engagement plus important.
                </div>
              )}
            </FaqItem>
          </FaqContainer>
        </Container>
      </FaqSection>
    </>
  );
};

export default CommunityPage;